import { useEffect, useRef, useState } from 'react';
import {
  approveTask,
  claimReview,
  escalateReview,
  fetchLatestTaskFromServer,
  releaseReview,
  requestChanges,
  reworkApprovedTask,
  returnSubmittedTaskToQueue,
} from '../../services/tasks';
import { Task } from '../../types';
import {
  ReviewController,
  ReviewControllerAction,
  ReviewControllerContext,
  ReviewControllerPorts,
} from './reviewController';
import { DraftSaveContext, TaskSessionActor } from './taskSessionController';

export interface UseTaskReviewControllerOptions {
  taskId: string | undefined;
  reviewRound: number | undefined;
  saveDraft: (context: DraftSaveContext) => Promise<void>;
  applyTaskSnapshot: (task: Task | null) => void;
  reportError?: (error: Error, action: ReviewControllerAction, taskId: string) => void;
}

function asFirebaseUser(actor: TaskSessionActor): Parameters<typeof claimReview>[1] {
  return {
    uid: actor.uid,
    email: actor.email ?? null,
    displayName: actor.displayName ?? null,
  } as Parameters<typeof claimReview>[1];
}

function createDefaultPorts(
  runtime: { current: Pick<UseTaskReviewControllerOptions, 'saveDraft' | 'applyTaskSnapshot' | 'reportError'> },
): ReviewControllerPorts {
  return {
    saveDraft: (context) => runtime.current.saveDraft(context),
    approveTask,
    requestChanges: (taskId, comment, expectedReviewRound) => requestChanges(taskId, {
      comment,
      expectedReviewRound,
    }),
    escalateReview,
    reworkApprovedTask,
    returnSubmittedTaskToQueue: (taskId, actor, expectedReviewRound) => (
      returnSubmittedTaskToQueue(taskId, asFirebaseUser(actor), expectedReviewRound)
    ),
    claimReview: (taskId, actor, expectedReviewRound) => (
      claimReview(taskId, asFirebaseUser(actor), expectedReviewRound)
    ),
    releaseReview,
    fetchLatestTask: fetchLatestTaskFromServer,
    applyTaskSnapshot: (task) => runtime.current.applyTaskSnapshot(task),
    reportError: (error, action, taskId) => runtime.current.reportError?.(error, action, taskId),
  };
}

export function useTaskReviewController(options: UseTaskReviewControllerOptions) {
  const runtimeRef = useRef<Pick<UseTaskReviewControllerOptions, 'saveDraft' | 'applyTaskSnapshot' | 'reportError'>>({
    saveDraft: options.saveDraft,
    applyTaskSnapshot: options.applyTaskSnapshot,
    reportError: options.reportError,
  });
  runtimeRef.current = {
    saveDraft: options.saveDraft,
    applyTaskSnapshot: options.applyTaskSnapshot,
    reportError: options.reportError,
  };

  const controllerRef = useRef<ReviewController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new ReviewController(createDefaultPorts(runtimeRef));
  }
  const controller = controllerRef.current;
  const [, setVersion] = useState(0);

  useEffect(() => controller.subscribe(() => setVersion((version) => version + 1)), [controller]);

  useEffect(() => {
    controller.updateContext(options.taskId, options.reviewRound);
  }, [controller, options.reviewRound, options.taskId]);

  useEffect(() => () => {
    void controller.dispose();
  }, [controller]);

  return {
    controller,
    ...controller.getSnapshot(),
    approve: (context: ReviewControllerContext) => controller.approve(context),
    requestChanges: (context: ReviewControllerContext, comment: string) => (
      controller.requestChanges(context, comment)
    ),
    escalate: (context: ReviewControllerContext, note: string) => (
      controller.escalate(context, note)
    ),
    rework: (context: ReviewControllerContext) => controller.rework(context),
    returnToQueue: (context: ReviewControllerContext) => controller.returnToQueue(context),
    claimForReview: (context: ReviewControllerContext) => controller.claimForReview(context),
    releaseFromReview: (context: ReviewControllerContext) => controller.releaseFromReview(context),
    clearError: () => controller.clearError(),
  };
}
